// viewport_audit — ce qui compte comme une casse d'écran.
//
// ── LA SÉPARATION QUI FAIT TOUT ─────────────────────────────────────────────
//
// Le navigateur MESURE (des boîtes, des tailles). Ce fichier JUGE. Rien ici ne touche au
// DOM, donc tout s'éprouve avec des boîtes fabriquées à la main — y compris les cas limites
// qu'un écran réel ne produit qu'une fois sur mille, et qui sont précisément ceux où une
// règle écrite à la va-vite se trompe.
//
// Demande de l'opérateur, le 2026-10-04 : « make the platform ui super responsive on all
// viewports ». Avant ce fichier, ce paquet ne pouvait que lire sa propre source : « c'est
// responsive » était une opinion.
//
// ── LES TROIS RÈGLES ────────────────────────────────────────────────────────
//
//   1. UNE PAGE NE DÉFILE JAMAIS DE CÔTÉ. Un défilement horizontal cache du contenu
//      derrière un geste que personne ne devine.
//   2. RIEN NE DÉPASSE LE BORD DROIT — la CAUSE de la règle 1. Elle nomme le coupable au
//      lieu de constater le symptôme.
//   3. UNE CIBLE TACTILE FAIT AU MOINS 24×24 px, avec son exception d'espacement.
//      WCAG 2.5.8, niveau AA, lu le 2026-10-04 et cité dans le test.

/** Un pixel, pas plus. Un navigateur rend des demi-pixels : sans tolérance le test crierait
 *  sur des pages saines, et avec davantage une vraie casse passerait. */
export const OVERFLOW_TOLERANCE_PX = 1;

/** WCAG 2.5.8 (AA) : « at least 24 by 24 CSS pixels ». */
export const TARGET_MIN_PX = 24;

/** Une boîte mesurée par le navigateur. `label` sert à NOMMER le coupable dans le rapport :
 *  un débordement anonyme ne se répare pas. */
export type Box = {
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

export type OverflowFinding = { readonly label: string; readonly overflowPx: number };
export type TargetFinding = { readonly label: string; readonly width: number; readonly height: number };

const nombre = (n: number): boolean => Number.isFinite(n);

/** Une boîte qui occupe de la place. Une largeur ou une hauteur nulle, c'est un panneau
 *  replié ou un menu fermé : il ne dépasse de rien, et le signaler enverrait réparer du vide. */
const visible = (b: Box): boolean =>
  nombre(b.x) && nombre(b.y) && nombre(b.width) && nombre(b.height) && b.width > 0 && b.height > 0;

/**
 * PURE. La page déborde-t-elle horizontalement ?
 *
 * Des valeurs illisibles rendent FAUX : un chiffre manquant est une mesure qui n'a pas eu
 * lieu, pas une casse — et un test qui échoue sur l'absence de mesure apprend à être ignoré.
 */
export function pageScrollsSideways(scrollWidth: number, clientWidth: number): boolean {
  if (!nombre(scrollWidth) || !nombre(clientWidth) || clientWidth <= 0) return false;
  return scrollWidth > clientWidth + OVERFLOW_TOLERANCE_PX;
}

/**
 * PURE. Ce qui sort du bord droit, du plus gros débordement au plus petit.
 *
 * LE BORD DROIT, PAS LA LARGEUR : une boîte de 200 px placée à 300 px dépasse d'une fenêtre
 * de 390, alors que sa largeur seule y tiendrait.
 *
 * Trié par débordement décroissant : on répare la cause, pas le bruit qu'elle produit.
 */
export function overflowFindings(boxes: readonly Box[], viewportWidth: number): OverflowFinding[] {
  if (!nombre(viewportWidth) || viewportWidth <= 0) return [];
  const out: OverflowFinding[] = [];
  for (const b of boxes) {
    if (!visible(b)) continue;
    const depassement = b.x + b.width - viewportWidth;
    if (depassement > OVERFLOW_TOLERANCE_PX) out.push({ label: b.label, overflowPx: Math.round(depassement) });
  }
  return out.sort((a, z) => z.overflowPx - a.overflowPx);
}

/** Le centre d'une boîte. */
const centre = (b: Box): { x: number; y: number } => ({ x: b.x + b.width / 2, y: b.y + b.height / 2 });

/** Un cercle touche-t-il un rectangle ? Le point du rectangle le plus proche du centre, puis
 *  la distance — la méthode usuelle, exacte y compris aux coins. */
function cercleToucheRect(cx: number, cy: number, r: number, b: Box): boolean {
  const px = Math.max(b.x, Math.min(cx, b.x + b.width));
  const py = Math.max(b.y, Math.min(cy, b.y + b.height));
  const dx = cx - px, dy = cy - py;
  return dx * dx + dy * dy < r * r;
}

/**
 * PURE. Les cibles trop petites QUI NE SONT PAS EXCUSÉES par l'espacement.
 *
 * L'exception de WCAG 2.5.8, appliquée telle qu'elle est écrite : « Undersized targets …
 * are positioned so that if a 24 CSS pixel diameter circle is centered on the bounding box
 * of each, the circles do not intersect another target or the circle for another undersized
 * target. »
 *
 * Donc un cercle de DIAMÈTRE 24 (rayon 12) centré sur la cible. Elle est excusée si ce
 * cercle ne touche ni une autre cible, ni le cercle d'une autre cible trop petite.
 *
 * SANS CETTE EXCEPTION, le test crierait sur des boutons conformes — et un test qui crie à
 * tort finit par être ignoré, ce qui est pire que pas de test.
 */
export function smallTargetFindings(targets: readonly Box[]): TargetFinding[] {
  const vues = targets.filter(visible);
  const petites = vues.filter((b) => b.width < TARGET_MIN_PX || b.height < TARGET_MIN_PX);
  const r = TARGET_MIN_PX / 2;
  const out: TargetFinding[] = [];
  for (const p of petites) {
    const c = centre(p);
    // Une autre cible touchée par le cercle…
    const toucheUneAutre = vues.some((autre) => autre !== p && cercleToucheRect(c.x, c.y, r, autre));
    // …ou le cercle d'une autre petite : deux cercles de rayon 12 se croisent si leurs
    // centres sont à moins de 24 px.
    const toucheUnCercle = petites.some((autre) => {
      if (autre === p) return false;
      const a = centre(autre);
      const dx = c.x - a.x, dy = c.y - a.y;
      return dx * dx + dy * dy < (r + r) * (r + r);
    });
    if (toucheUneAutre || toucheUnCercle) out.push({ label: p.label, width: p.width, height: p.height });
  }
  // Ordre STABLE : un rapport qui change d'ordre d'un passage à l'autre est impossible à
  // comparer, et personne ne voit ce qui a bougé.
  return out.sort((a, z) => a.label.localeCompare(z.label));
}
